import { Check } from 'lucide-react';
import { Button } from './ui/button';
import { getPlanBadgeStyle } from '../lib/usage';
import {
  CREDIT_USAGE_ITEMS,
  PLAN_CAPABILITY_COPY,
  type DisplayPaidPlan,
  type PaidPlanType,
} from '../lib/plan-marketing';

interface PlanComparisonProps {
  plans: DisplayPaidPlan[];
  layout?: 'comparison' | 'focused';
  currentPlanType?: string;
  pendingPlanType?: string | null;
  recommendedPlanType?: PaidPlanType;
  loadingPlanType?: string | null;
  disabled?: boolean;
  getActionLabel?: (plan: DisplayPaidPlan) => string;
  onSelect: (plan: DisplayPaidPlan) => void;
}

export function PlanComparison({
  plans,
  layout = 'comparison',
  currentPlanType,
  pendingPlanType,
  recommendedPlanType = 'pro',
  loadingPlanType,
  disabled = false,
  getActionLabel,
  onSelect,
}: PlanComparisonProps) {
  return (
    <div
      className={`grid grid-cols-1 gap-3 ${
        layout === 'focused' ? 'mx-auto w-full max-w-md' : 'xl:grid-cols-3'
      }`}
    >
      {plans.map((plan) => {
        const isCurrent = plan.type === currentPlanType;
        const isPending = plan.type === pendingPlanType;
        const isRecommended = plan.type === recommendedPlanType;
        const isLoading = loadingPlanType === plan.type;

        return (
          <article
            key={plan.type}
            className={`relative flex min-h-[490px] flex-col overflow-hidden rounded-xl border bg-white/[0.025] transition-colors ${
              isRecommended
                ? 'border-white/25 bg-white/[0.045]'
                : 'border-white/10 hover:border-white/20'
            }`}
          >
            <div
              aria-hidden="true"
              className={`h-1 w-full ${
                plan.type === 'pro'
                  ? 'bg-blue-400'
                  : plan.type === 'business'
                    ? 'bg-violet-400'
                    : 'bg-amber-400'
              }`}
            />

            <div className="px-5 pb-4 pt-5">
              <div className="flex min-h-7 items-center justify-between gap-3">
                <span
                  className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${getPlanBadgeStyle(plan.type)}`}
                >
                  {plan.name}
                </span>
                {isCurrent ? (
                  <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-900">
                    Current plan
                  </span>
                ) : isPending ? (
                  <span className="rounded-full bg-blue-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white">
                    Scheduled
                  </span>
                ) : isRecommended ? (
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">
                    Most popular
                  </span>
                ) : null}
              </div>

              <div className="mt-5 flex items-end">
                <span className="text-[42px] font-bold leading-none tracking-[-0.04em] text-white tabular-nums">
                  ${plan.price}
                </span>
                <span className="mb-0.5 ml-1 text-sm text-white/45">
                  / month
                </span>
              </div>

              <p className="mt-5 text-sm font-semibold text-white">
                {plan.marketing.designsPerMonth}
              </p>
              <p className="mt-0.5 text-xs text-white/45">
                {plan.creditsPerMonth.toLocaleString()} credits included
              </p>
            </div>

            <p className="min-h-[116px] border-y border-white/10 bg-black/10 px-5 py-4 text-sm leading-6 text-white/58">
              {plan.marketing.intro}
            </p>

            <ul className="space-y-3 px-5 py-4 text-sm leading-5 text-white/72">
              {plan.marketing.bullets.map((bullet) => (
                <li key={bullet} className="grid grid-cols-[16px_1fr] gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 text-blue-300/85" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto px-5 pb-5 pt-2">
              <Button
                variant={isRecommended && !isCurrent ? 'default' : 'outline'}
                className="w-full whitespace-normal"
                disabled={
                  disabled || Boolean(loadingPlanType) || isCurrent || isPending
                }
                onClick={() => onSelect(plan)}
              >
                {isLoading ? (
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-current" />
                  </span>
                ) : isCurrent ? (
                  <>
                    <Check className="h-4 w-4" />
                    Current plan
                  </>
                ) : isPending ? (
                  'Scheduled'
                ) : (
                  (getActionLabel?.(plan) ?? plan.marketing.cta)
                )}
              </Button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export function PlanIntro() {
  return (
    <p className="max-w-4xl text-sm leading-6 text-white/60">
      <span className="font-semibold text-white/85">
        Every plan includes full access to Miles. No coding agent subscription
        required.
      </span>{' '}
      {PLAN_CAPABILITY_COPY.replace(
        'Every plan includes full access to Miles. No coding agent subscription required. ',
        '',
      )}
    </p>
  );
}

export function CreditUsageGrid() {
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-white/10 bg-white/10 lg:grid-cols-4">
      {CREDIT_USAGE_ITEMS.map((item) => (
        <div key={item.title} className="bg-neutral-950/70 px-4 py-3">
          <p className="text-base font-bold tracking-tight text-white">
            {item.credits}{' '}
            <span className="text-xs font-medium text-white/45">credits</span>
          </p>
          <p className="mt-1 text-xs font-semibold text-white/80">
            {item.title}
          </p>
          <p className="mt-0.5 text-[11px] leading-4 text-white/40">
            {item.description}
          </p>
        </div>
      ))}
    </div>
  );
}
